import { alternateUrl } from '@/services/region'

/**
 * One retry on the other zone when a creative will not load.
 *
 * Until 23.09.2026 none of the three ad components had an `@error` handler on
 * its `<img>` at all - measured, zero across every component and page in this
 * repository. A creative that failed to load produced a countdown over an empty
 * frame: the ad was reported as shown, the streamer saw nothing, and no failure
 * report was sent. Only `<video>` had an error path.
 *
 * The retry is deliberately one, and deliberately the *other* zone rather than
 * the same URL again. A creative that 404s is not going to appear on a second
 * attempt; what does happen is a zone being briefly unreachable - the RU mirror
 * reaching S3 across a border, or the global CDN from inside Russia - and for
 * that, the same object under the other hostname is the answer. Both names are
 * served from one S3 prefix, so the object is the same file either way.
 *
 * Whoever consumes this owns the reporting: the component knows which failure
 * reason and which signed URL applies, and reporting twice for one creative
 * would be worse than the silence this replaces.
 */
export default {
  data () {
    return {
      mediaSrcOverride: null,
      mediaSrcsetOverride: null,
      mediaFallbackTried: false
    }
  },

  computed: {
    /** What the template binds instead of `contentUrl`. */
    resolvedContentUrl () {
      return this.mediaSrcOverride || this.contentUrl
    },

    /** The same, for components that carry a per-type source set. */
    resolvedContentSrcset () {
      return this.mediaSrcsetOverride || this.contentSrcset || {}
    }
  },

  methods: {
    /**
     * @returns {boolean} true when a retry was scheduled and the caller should
     *                    wait for it rather than giving up on the ad.
     */
    tryMediaFallback () {
      if (this.mediaFallbackTried) return false

      const current = this.resolvedContentUrl
      const next = alternateUrl(current)

      if (!next || next === current) return false

      this.mediaFallbackTried = true
      this.mediaSrcOverride = next

      const set = this.contentSrcset

      if (set && Object.keys(set).length) {
        const mapped = {}

        for (const [type, url] of Object.entries(set)) {
          mapped[type] = alternateUrl(url) || url
        }

        this.mediaSrcsetOverride = mapped
      }

      console.warn('creative did not load, retrying on the other zone', { from: current, to: next })

      // A <video> keeps serving the old source list until it is told to re-read
      // it; an <img> reloads on its own when src changes.
      this.$nextTick(() => {
        const el = this.$refs.videoElement

        if (el && typeof el.load === 'function') el.load()
      })

      return true
    }
  }
}
